// src/pwa/hooks/usePwaPushToken.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../firebase/firebase';

import {
  hasNotification,
  getPermissionSafe,
  requestPermissionSafe,
} from '../utils/notifySafe';
import { urlBase64ToUint8Array } from '../utils/urlBase64ToUint8Array';

export function usePwaPushToken(uid) {
  const [permission, setPermission] = useState(getPermissionSafe());
  const [needAction, setNeedAction] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  // 多重実行/冪等制御用
  const alertedOnSnapshotRef = useRef(false);
  const alertedAutoRecoverRef = useRef(false);
  const alertedSubscribeRef = useRef(false);
  const alertedSaveCallRef = useRef(false);

  // ① VAPID 公開鍵（env を初期値に）
  const [vapidKey, setVapidKey] = useState(
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEBPUSH_PUBLIC_KEY) || ''
  );

  // ② env に無ければ Functions から取得
  useEffect(() => {
    (async () => {
      if (vapidKey) return;
      try {
        const fn = httpsCallable(functions, 'getWebPushPublicKey');
        const { data } = await fn();
        const pub = data?.publicKey ? String(data.publicKey) : '';
        if (!pub) throw new Error('missing public key from functions');
        setVapidKey(pub);
        console.debug('[push] fetched VAPID public key from Functions');
      } catch (e) {
        setNeedAction(true);
        setReason(e?.message || 'failed to load VAPID public key');
        console.warn('[push] getWebPushPublicKey failed:', e);
      }
    })();
  }, [vapidKey]);

  // 現在の SW 購読を読む（ある/ない）だけ
  const readLocalSubscription = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/pwa/');
      if (!reg) return null;
      return await reg.pushManager.getSubscription(); // PushSubscription | null
    } catch {
      return null;
    }
  };

  // 追加: 制御フラグ & デバウンス
const recoveringRef = useRef(false);
const debounceTimerRef = useRef(null);


  // ③ 購読を作って Functions に保存
  const refreshAndSave = useMemo(() => {
    return async () => {
      setBusy(true);
      setReason('');
      recoveringRef.current = true;     
      try {
        if (!('serviceWorker' in navigator)) throw new Error('no service worker');
        if (!hasNotification()) throw new Error('Notification API not available');
        if (!vapidKey) throw new Error('missing VAPID public key');

        // SW を確保
        let reg = await navigator.serviceWorker.getRegistration('/pwa/');
        if (!reg) {
          reg = await navigator.serviceWorker.register('/pwa/sw.js', { scope: '/pwa/' });
          try { await navigator.serviceWorker.ready; } catch {}
        }

        // 既存購読 or 新規購読
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          if (!alertedSubscribeRef.current) alertedSubscribeRef.current = true;
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
          });
        }

        // サーバへ保存（pwa_users/{uid}.wpSubs に追記される onCall）
        if (!alertedSaveCallRef.current) alertedSaveCallRef.current = true;
        const save = httpsCallable(functions, 'saveSafariSubscription');

        // PushSubscription → プレーン JSON
        const json = (typeof sub?.toJSON === 'function')
          ? sub.toJSON()
          : {
              endpoint: sub?.endpoint || null,
              keys: (sub?.getKey
                ? {
                    p256dh: sub.getKey('p256dh')
                      ? btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh'))))
                      : null,
                    auth: sub.getKey('auth')
                      ? btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))))
                      : null,
                  }
                : undefined),
            };
        if (!json?.endpoint) throw new Error('subscription has no endpoint');

        await save({ subscription: json });

        // ★ 楽観更新：保存成功 → すぐにバナーを閉じる
        setSubscribed(true);
        setNeedAction(false);
        setReason('');

        return 'webpush-subscribed';
      } catch (e) {
        setSubscribed(false);
        setNeedAction(true);
        setReason(e?.message || 'subscription failed');
        throw e;
      } finally {
        setTimeout(() => { recoveringRef.current = false; }, 800);
        setBusy(false);
      }
    };
  }, [vapidKey]);


// 端末ローカルの購読を state で保持
const [localHasSub, setLocalHasSub] = useState(false);

// ローカル購読を独立して監視（起動時＋visibilitychange）
useEffect(() => {
  let canceled = false;
  const readLocal = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/pwa/');
      const sub = await reg?.pushManager.getSubscription();
      if (!canceled) setLocalHasSub(!!sub);
    } catch {
      if (!canceled) setLocalHasSub(false);
    }
  };
  readLocal();

  const onVis = () => {
    setPermission(getPermissionSafe());
    readLocal();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => { canceled = true; document.removeEventListener('visibilitychange', onVis); };
}, []);

const lastShouldShowRef = useRef(null);

// Firestore: pwa_users/{uid} を監視
useEffect(() => {
  if (!uid) return;
  const ref = doc(db, 'pwa_users', uid);

  const unsub = onSnapshot(
    ref,
    async (snap) => {
      // キャッシュ結果は無視（点滅の元）
      if (snap.metadata?.fromCache) return;

      const d = snap.data() || {};
      const subs = Array.isArray(d.wpSubs) ? d.wpSubs : [];
      const fcm  = Array.isArray(d.fcmTokens) ? d.fcmTokens.filter(x => typeof x === 'string' && x) : [];
      setPermission(getPermissionSafe());

      const serverMissing = (subs.length === 0 && fcm.length === 0);
      const shouldShow = serverMissing || !localHasSub; // ← サーバ or ローカルどちらか欠けたらバナー

      // 復旧中は UI を触らない（Functionsへの保存が確定するまで待つ）
      if (recoveringRef.current) return;

      // デバウンス
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(async () => {
        // 直前と同じなら何もしない（無駄な再レンダ防止）
        if (lastShouldShowRef.current === shouldShow) return;
        lastShouldShowRef.current = shouldShow;

        if (shouldShow) {
          setNeedAction(true);
          setReason(serverMissing ? 'server has no subscription' : 'no local subscription');

          // 既に通知許可があるなら静かに自動復旧
          if (getPermissionSafe() === 'granted') {
            try { await refreshAndSave(); } catch {/* noop */}
          }
        } else {
          setNeedAction(false);
          setReason('');
        }
      }, 400);
    },
    (err) => {
      console.warn('[push] onSnapshot(pwa_users) error:', err?.message || err);
      setNeedAction(true);
      setReason('firestore read error');
    }
  );

  return () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    unsub();
  };
}, [uid, refreshAndSave, localHasSub]);

  // ユーザー操作：権限要求 → 購読 → 保存
  const requestAndRegister = async () => {
    if (!hasNotification()) {
      setNeedAction(true);
      setReason('Notification API not available');
      return;
    }
    setBusy(true);
    recoveringRef.current = true;
    try {
      const p = await requestPermissionSafe();
      setPermission(p);
      if (p === 'granted') {
        await refreshAndSave();

        try {
           // 送信直前に必ず JSON 化する
          const reg = await navigator.serviceWorker.getRegistration('/pwa/');
          const sub = await reg?.pushManager.getSubscription();
          if (!sub) throw new Error('no local subscription');

          const subscription = (typeof sub.toJSON === 'function')
            ? sub.toJSON()
            : { endpoint: sub.endpoint }; // 最低限

          const call = httpsCallable(functions, 'sendWebpushTest');
          const { data } = await call({
            subscription,
            title: '疎通確認',
            body:  '【設定完了】受付時、キャンセル時、順番が5人前になると通知されます',
            link:  window.location.origin + '/pwa/'
          });
          console.log('sendWebpushTest:', data);
        } catch (e) {
          // テスト送信が失敗しても購読自体は完了しているので UI はふさがない
          console.warn('[push] sendWebpushTest failed:', e);
          setReason(e?.message || 'test send failed');
          // ここで setNeedAction(true) にするかはお好み（通知ボタンを再表示させたいなら true）
        }
      } else {
        setNeedAction(true);
        setReason(`permission: ${p}`);
      }
    } catch (e) {
      setNeedAction(true);
      setReason(e?.message || 'request/register failed');
    } finally {
      setBusy(false);
    }
  };

  // 画面復帰で権限の変化を反映
  useEffect(() => {
    const onVis = () => {
      const p = getPermissionSafe();
      setPermission(p);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  return {
    permission,
    subscribed,
    busy,
    needAction,
    reason,
    refreshAndSave,
    requestAndRegister,
  };
}